import { useState, useEffect, useRef } from 'react';
import EmptyState from '../components/EmptyState';
import InlineLoadError from '../components/InlineLoadError';
import NoAccessNotice from '../components/NoAccessNotice';
import { useParams, useNavigate, useLocation } from '../routing';
import { suppliersApi, documentsApi, formatCurrency, downloadDocFile, statusBadge } from '../utils/api';
import { toast } from '../components/Toast';
import CommentsModal, { DRAFT_INTERNAL_ONLY_REASON } from '../components/CommentsModal';
import PaymentModal from '../components/PaymentModal';
import { useAuth } from '../context/AuthContext';
import { DocPriceTags } from '../components/PriceSourceBadge';
import { useSettings } from '../context/SettingsContext';
import { paymentsApi } from '../utils/api';
import { formatDateShort } from '../utils/dateFormat';
import { formatUtcDay as formatUtcDate } from '../utils/dateFormat';
import Icon from '../components/Icon';
import PeriodPicker from '../components/PeriodPicker';
import PaymentDetailPanel from '../components/PaymentDetailPanel';
import { PaymentLifecycleControl } from '../components/PaymentLifecycleBadge';
import DocStatusControl from '../components/DocStatusControl';
import { approvalOfferForDoc } from '../utils/paymentLifecycle';
import { isCreditNoteDoc, isPayableDocType, SUPPLIER_DOC_TYPES } from '../utils/docTypes';
import { useConfirm } from '../utils/useConfirm';
import { PRESETS_MONTHLY } from '../utils/period';
import { HqOnlyBadge } from '../components/HqOnlyNotice';
import { ViewAsMenuItems } from '../components/ViewAsMenu';
import LedgerViewer from '../components/LedgerViewer';
import DeleteDocumentDialog from '../components/DeleteDocumentDialog';
import LinkedNoteMark from '../components/LinkedNoteMark';
import { useIsMobile } from '../utils/useIsMobile';

// The filter offers exactly what the page lists — see SUPPLIER_DOC_TYPES.
const DOC_TYPES = SUPPLIER_DOC_TYPES;

// What "צפה בהערות (N)" promises. Notes reach a document from two places —
// הוסף הערה (the comments table) and בוצע תשלום (payments.notes) — and the
// modal shows both, so the badge has to count both or it undercounts exactly
// the notes that explain the money.
const noteCount = (d) => (d.comment_count || 0) + (d.payment_note_count || 0);

export default function SupplierDocumentsPage() {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit, isAdmin } = useAuth();
  const isMobile = useIsMobile();
  const { paymentTrackingEnabled } = useSettings();

  // Pick up date_from / date_to forwarded from the suppliers page so the user
  // lands on the same period they were viewing. Computed from location.search
  // so the same logic powers both the initial useState seed and the
  // location-change useEffect below.
  //
  // No month is derived here any more: the range IS the single source of truth,
  // and PeriodPicker works out for itself whether it names a calendar month
  // (deriveMonthSpan) and opens on the grid or the day calendar accordingly.
  // The old <input type="month"> next to two <input type="date"> fields was a
  // second copy of that state, which is why it needed seeding to stay in sync.
  const datesFromUrl = (search) => {
    const qp = new URLSearchParams(search);
    const dateRe = /^\d{4}-\d{2}-\d{2}$/;
    const from = dateRe.test(qp.get('date_from') || '') ? qp.get('date_from') : '';
    const to   = dateRe.test(qp.get('date_to')   || '') ? qp.get('date_to')   : '';
    return { from, to };
  };
  const initialDates = datesFromUrl(location.search);

  // Optional due_date display banner — set when navigating from /suppliers
  // with the "לתשלום ב" filter active. The date_from/date_to in the URL
  // already restrict the docs to that month; this is for display only.
  const dueDateFromUrl = (() => {
    const qp = new URLSearchParams(location.search);
    const v = qp.get('due_date') || '';
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '';
  })();
  const fmtIsoToDisplay = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y.slice(-2)}`;
  };

  const [supplier, setSupplier] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [accessDenied, setAccessDenied] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(initialDates.from);
  const [dateTo, setDateTo] = useState(initialDates.to);
  const [openMenu,      setOpenMenu]      = useState(null);
  // { id, top, right } — the popover is position:fixed at the badge, because
  // the table's scroll box clips anything absolute inside it (last rows, phones).
  const [closedCreditsOpen, setClosedCreditsOpen] = useState(null);
  // A fixed popover would float away from its badge on scroll — close it instead.
  useEffect(() => {
    if (!closedCreditsOpen) return undefined;
    const close = () => setClosedCreditsOpen(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [closedCreditsOpen]);
  // The document whose payment panel is open, plus the badge's rect so the
  // desktop popover can anchor to it. Mobile ignores the rect (bottom sheet).
  const [paymentPanel,  setPaymentPanel]  = useState(null);
  const [pageMenuOpen,  setPageMenuOpen]  = useState(false);
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  // The latest כרטסת (supplier.latest_ledger): its menu, and its scan viewer.
  const [ledgerMenuOpen, setLedgerMenuOpen] = useState(false);
  const [ledgerViewOpen, setLedgerViewOpen] = useState(false);
  // Replaces window.confirm for the two destructive row actions — see
  // utils/useConfirm.jsx for why the native dialog had to go.
  const [askConfirm, confirmDialog] = useConfirm();
  const [modal,         setModal]         = useState(null);
  const [paymentModal,  setPaymentModal]  = useState(null);
  // Delivery notes of this supplier that look like they are already billed by
  // an invoice and are not linked to it. null = nothing to say (or not an
  // admin). See server/lib/unlinkedDeliveryNotes.js — while the link is missing
  // BOTH documents count in full and this supplier's balance reads high.
  // `linkable` says whether the matched invoice is a חשבונית מרכזת, the only
  // type that can absorb the notes; the other cover types are detect-only.
  const [unlinked, setUnlinked] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  // This month's payment lifecycle for the header — the same object the
  // /suppliers row shows, from GET /suppliers/:id?date_from&date_to. null when
  // the range names no single month (then there is nothing to approve).
  const [lifecycle, setLifecycle] = useState(null);
  const [approving, setApproving] = useState(false);

  // Token guard against out-of-order responses. This page stays MOUNTED when
  // the user moves supplier -> supplier (the effect just re-runs on
  // supplierId), so a slow response for the supplier they left can land after
  // the fast one for the supplier they opened and repaint the page with the
  // wrong supplier's documents. Mirrors SuppliersPage / CreditsHistoryPage.
  const loadToken = useRef(0);
  const load = async () => {
    const token = ++loadToken.current;
    setLoading(true);
    setLoadError(false);
    setAccessDenied('');
    try {
      const [supRes, docsRes] = await Promise.all([
        suppliersApi.getOne(supplierId),
        documentsApi.getBySupplier(supplierId),
      ]);
      if (token !== loadToken.current) return;   // superseded
      setSupplier(supRes.data);
      setDocs(docsRes.data);
      loadUnlinked();
    } catch (err) {
      // Tenant switch on /supplier/:id sends the new x-restaurant-id with the
      // old supplierId — the server returns 403 because the supplier belongs
      // to a different restaurant. Same for 404 if the supplier was deleted.
      // Either way the right move is to land on the suppliers list of the
      // newly-active tenant instead of leaving an unhandled error overlay.
      if (token !== loadToken.current) return;
      const status = err?.response?.status;
      if (err?.response?.data?.code === 'HQ_ONLY_SUPPLIER') {
        setAccessDenied(err.response.data.error);
        return;
      }
      if (status === 403 || status === 404) {
        navigate('/suppliers', { replace: true });
        return;
      }
      // Anything else is a real load failure: surface it in place rather
      // than toasting and letting the empty state claim there are no
      // documents (see InlineLoadError).
      setLoadError(true);
    } finally {
      if (token === loadToken.current) setLoading(false);
    }
  };

  // Its own fetch, deliberately NOT part of the Promise.all above and never
  // allowed to fail the page: this is a side signal, and a document list that
  // refuses to render because an advisory flag 404'd would be a worse product
  // than one that quietly omits the flag. Admin-only — the server 403s anyone
  // else, because only an admin can link (PUT /documents/:id/links is
  // requireAdmin) and flagging work at someone who cannot do it is noise.
  const loadUnlinked = async () => {
    if (!isAdmin) return;
    try {
      const res = await suppliersApi.unlinkedDeliveryNotes({ supplier_id: supplierId });
      setUnlinked(res.data?.[0] || null);
    } catch {
      setUnlinked(null);
    }
  };

  // Its own fetch, keyed on the period: the document list does not change when
  // the user moves months (it is fetched once per supplier and filtered on the
  // client), but the lifecycle is a fact about ONE month, so it has to follow
  // the picker. Never allowed to fail the page — it is a header badge.
  const lifecycleToken = useRef(0);
  const loadLifecycle = async () => {
    const token = ++lifecycleToken.current;
    if (!dateFrom || !dateTo) { setLifecycle(null); return; }
    try {
      const res = await suppliersApi.getOne(supplierId, { date_from: dateFrom, date_to: dateTo });
      if (token !== lifecycleToken.current) return;
      setLifecycle(res.data?.lifecycle ?? null);
    } catch {
      if (token !== lifecycleToken.current) return;
      setLifecycle(null);
    }
  };
  useEffect(() => { loadLifecycle(); }, [supplierId, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Approve / cancel this month from the header — same endpoint the list uses;
  // the response carries the new lifecycle, so nothing is recomputed here.
  const toggleApproval = async () => {
    if (approving || !lifecycle) return;
    setApproving(true);
    try {
      const res = await suppliersApi.approve(supplierId, dateFrom, dateTo);
      if (res.data?.lifecycle) setLifecycle(res.data.lifecycle);
    } catch (err) {
      toast(err?.response?.data?.error || 'שגיאה בעדכון אישור', 'error');
    } finally {
      setApproving(false);
    }
  };

  // "These belong to next month's invoice." Dismissal is per NOTE on the
  // server, so every note this indicator is counting is marked at once —
  // dismissing "the message" while leaving half its notes flagged would bring
  // the same indicator straight back and read as the button not working.
  const dismissUnlinked = () => {
    if (!unlinked) return;
    const n = unlinked.note_count;
    askConfirm({
      // The question and the button must agree. An earlier pair asked "do they
      // belong to next month?" over a button reading "mark that they don't
      // belong" — the user answered yes by pressing no. The button now repeats
      // the answer, which is also what makes it safe to default-focus.
      title: n === 1
        ? 'לסמן שהתעודה שייכת לחשבונית של החודש הבא?'
        : 'לסמן שהתעודות שייכות לחשבונית של החודש הבא?',
      // Says how to undo it, in the same breath as the warning. Linking the note
      // later clears the mark on its own, so this is reversible in practice even
      // though there is no undo button.
      // The "you can always link it later" escape only exists for a חשבונית
      // מרכזת. Promising it against a plain חשבונית would be describing an undo
      // the app does not have.
      message: unlinked.linkable
        ? (n === 1
          ? 'התעודה לא תופיע יותר בהודעה הזו. תמיד אפשר לקשר אותה מאוחר יותר מתוך החשבונית המרכזת — הקישור מבטל את הסימון.'
          : `${n} תעודות לא יופיעו יותר בהודעה הזו. תמיד אפשר לקשר אותן מאוחר יותר מתוך החשבונית המרכזת — הקישור מבטל את הסימון.`)
        : (n === 1
          ? 'התעודה לא תופיע יותר בהודעה הזו.'
          : `${n} תעודות לא יופיעו יותר בהודעה הזו.`),
      detail: `${supplier?.name || ''} · ${formatCurrency(unlinked.total_amount)}`,
      // Not the default 'success': a green tick overstates a one-way action that
      // hides money from a warning. Neutral, not alarming — it is a legitimate
      // answer, just not a celebratory one.
      tone: 'neutral',
      confirmLabel: n === 1 ? 'כן, שייכת לחודש הבא' : 'כן, שייכות לחודש הבא',
      onConfirm: async () => {
        setDismissing(true);
        try {
          await Promise.all((unlinked.notes || []).map(
            note => documentsApi.dismissLink(note.id, note.invoice_id)
          ));
          setUnlinked(null);
        } catch {
          toast('לא הצלחנו לעדכן — נסי שוב', 'error');
        } finally {
          setDismissing(false);
        }
      },
    });
  };

  useEffect(() => { load(); }, [supplierId, location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply URL date params when location.search changes — covers the case
  // where the user lands here via a route the React Router instance was
  // already mounted on (e.g. /supplier/14 → /supplier/15?date_from=...),
  // where the useState initializer above runs once and would otherwise miss
  // the new query string.
  useEffect(() => {
    const next = datesFromUrl(location.search);
    setDateFrom(next.from);
    setDateTo(next.to);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (docId, status) => {
    const r = await documentsApi.updateStatus(docId, status);
    // A draft with an opened credit cannot become תקין: the server writes
    // לא תקין and says so in `status` (server/lib/creditGuard.js, #260).
    const written = r?.data?.status;
    toast(written && written !== status ? 'יש זיכוי פתוח — המסמך נשמר כלא תקין' : 'סטטוס עודכן');
    load();
  };

  // Takes the whole row, not just its id: the dialog names what it is about to
  // destroy, which is the entire point of replacing the native prompt.
  const docLabel = (d) => `${d.doc_type}${d.doc_num ? ` · ${d.doc_num}` : ''}`;

  const handleDeletePayment = (d) => {
    // DELETE /payments/document/:id removes EVERY payment on the document, and
    // since partial payments a document can carry several. The old copy said
    // "רישום התשלום" — singular — which understated what the click does.
    const n = d.payment_count || 0;
    askConfirm({
      title: n > 1 ? 'מחיקת התשלומים' : 'מחיקת תשלום',
      message: n > 1
        ? `למחוק את ${n} התשלומים שנרשמו על ${docLabel(d)}?`
        : `למחוק את התשלום שנרשם על ${docLabel(d)}?`,
      detail: `${formatCurrency(d.payment_paid_total)} יימחקו, והמסמך יחזור להיות פתוח לתשלום. הפעולה נרשמת ביומן.`,
      confirmLabel: n > 1 ? 'מחק תשלומים' : 'מחק תשלום',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await paymentsApi.deleteByDocument(d.id);
          toast(n > 1 ? 'רישומי התשלום נמחקו' : 'רישום התשלום נמחק');
          load();
        } catch { toast('שגיאה במחיקת תשלום', 'error'); }
      },
    });
  };

  // The reason dialog (components/DeleteDocumentDialog.jsx) owns the request
  // and its toast. A paid document is the case worth pausing on — its
  // payments leave the supplier's figures with it (and come back on a
  // restore), which is not obvious from "delete document". The verb agrees
  // with the count — "רשומים תשלום" (plural verb, singular noun) is not
  // Hebrew, so the whole clause switches, not just the noun.
  const [deleting, setDeleting] = useState(null);
  const handleDelete = (d) => setDeleting(d);
  const deletingContext = (d) => {
    const n = d.payment_count || 0;
    return n > 0
      ? `${n === 1 ? 'למסמך רשום תשלום' : `למסמך רשומים ${n} תשלומים`} בסך ${formatCurrency(d.payment_paid_total)}.`
      : '';
  };

  const clearFilters = () => {
    setTypeFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const fromStr = dateFrom ? dateFrom.replace(/-/g, '') : '';
  const toStr   = dateTo   ? dateTo.replace(/-/g, '')   : '';

  const filtered = docs.filter(d => {
    if (typeFilter && d.doc_type !== typeFilter) return false;
    if (fromStr || toStr) {
      // Convert stored DD/MM/YYYY → YYYYMMDD for lexicographic comparison
      const docDate = d.date ? d.date.split('/').reverse().join('') : '';
      if (fromStr && docDate < fromStr) return false;
      if (toStr   && docDate > toStr)   return false;
    }
    return true;
  });

  // Mirrors CREDIT in server/lib/amountOwed.js — doc_type OR a negative total.
  // The sign half matters because suppliers issue credits as negative invoices
  // on their normal invoice series, which the OCR files as 'חשבונית'.
  const isCreditDoc = d => ['חשבונית זיכוי', 'תעודת החזרה'].includes(d.doc_type) || (d.total || 0) < 0;
  // A תעודת משלוח OR תעודת החזרה already linked to a חשבונית מרכזת is rolled up
  // under that invoice's total. Counting it again here double-counts the
  // supplier — and for a תעודת החזרה it double-counts the CREDIT, since the
  // check below would also subtract it. Backend exposes `linked_to_invoice`
  // (1/0) per row for exactly this case; this test MUST stay above the credit
  // branch, matching the rule order in server/lib/amountOwed.js.
  const signedTotal = (d, field) => {
    if (d.linked_to_invoice) return 0;
    // A credit nets only the part of it still OUTSTANDING — fully settled is a
    // closed transaction and reduces nothing, untouched nets in full, partly
    // settled nets its remainder. Mirrors OWED_AMOUNT_CASE in
    // server/lib/amountOwed.js; this footer and the suppliers page must agree.
    //
    // The pre-VAT and VAT columns scale by the same fraction as the total,
    // because a part-settlement isn't attributable to one or the other. With no
    // payments the ratio is 1 and with a full one it is 0, so untouched and
    // settled credits behave exactly as they did before partial payments.
    //
    if (isCreditDoc(d)) {
      const magnitude = Math.abs(d.total || 0);
      if (magnitude === 0) return 0;
      // חשבונית זיכוי is the one exception (owner, 2026-09-15): the total
      // always nets its FULL value — recording it moves it from לתשלום to
      // שולם, not out of the total — so its ratio stays 1 even once
      // payment_remaining drops to 0. Mirrors OWED_AMOUNT_CASE's CREDIT_NOTE
      // branch in server/lib/amountOwed.js.
      const remaining = isCreditNoteDoc(d)
        ? magnitude
        : (d.payment_remaining != null ? d.payment_remaining : magnitude);
      const ratio = Math.min(1, Math.max(0, remaining / magnitude));
      return -Math.abs(d[field] || 0) * ratio;
    }
    return d[field] || 0;
  };
  const totalAmount   = filtered.reduce((s, d) => s + signedTotal(d, 'total'),    0);
  const totalSubtotal = filtered.reduce((s, d) => s + signedTotal(d, 'subtotal'), 0);
  const totalVat      = filtered.reduce((s, d) => s + signedTotal(d, 'vat'),      0);
  const hasFilters = typeFilter || dateFrom || dateTo;

  if (accessDenied) return <NoAccessNotice message={accessDenied} />;

  return (
    <div>
      <div className="breadcrumb">
        <span className="link" onClick={() => navigate('/')}>ספקים</span>
        <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
        <span>{supplier?.name || '...'}</span>
      </div>

      {/* Page-level actions live on the title line as [דוחות] [⋮], the same
          shape as /suppliers and /suppliers/paid.
          The two reports share ONE button rather than sitting side by side:
          "ייצוא" and "ייצוא תשלומים" as separate chips said nothing about how
          they differ, so picking between them meant clicking one to find out.
          Named reports in a menu answer that before the download.
          העלאת מסמך is in the ⋮ ONLY, with no button of its own — it is
          admin-only (documents are an admin area in server/lib/roles.js), so a
          dedicated button would be chrome that most of the team never sees and
          that the one role who does can find where every other page-level
          action lives. /suppliers treats + הוסף ספק exactly this way. It used
          to sit in an .action-row between the filters and the table, which also
          read as belonging to the card below it rather than to the supplier. */}
      <div className="page-title-row">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {supplier?.name}
          {supplier?.hq_only ? <HqOnlyBadge /> : null}
          {/* The month's payment lifecycle, so the drill-in reads the same as
              the list row it was opened from — and can be approved from here
              (a wider window approves each of its months). */}
          {paymentTrackingEnabled && lifecycle && (
            <span style={{ fontSize: '0.85rem', fontWeight: 400 }}>
              <PaymentLifecycleControl
                lifecycle={lifecycle}
                canApprove={canEdit}
                busy={approving}
                onToggle={toggleApproval}
              />
            </span>
          )}
        </h1>
        <div className="page-title-actions">
          {/* The supplier's latest approved כרטסת, whatever month is shown —
              ledgers never enter the documents table (they are not purchases);
              older ones are on /documents. */}
          {supplier?.latest_ledger && (
            <div className="page-menu">
              <button className="btn btn-sm btn-secondary"
                aria-expanded={ledgerMenuOpen}
                onClick={() => setLedgerMenuOpen(o => !o)}>
                <Icon name="list" size={14} />
                כרטסת אחרונה {supplier.latest_ledger.date}
              </button>
              {ledgerMenuOpen && (
                <>
                  <div className="menu-backdrop" onClick={() => setLedgerMenuOpen(false)} />
                  <div className="page-menu-panel">
                    <button className="page-menu-item"
                      onClick={() => { setLedgerMenuOpen(false); setLedgerViewOpen(true); }}>
                      <Icon name="eye" size={15} /> צפה
                    </button>
                    <button className="page-menu-item"
                      onClick={() => { setLedgerMenuOpen(false); navigate(`/document/${supplier.latest_ledger.id}`); }}>
                      <Icon name="external" size={15} /> פתח מסמך
                    </button>
                  </div>
                </>
              )}
              {ledgerViewOpen && (
                <LedgerViewer docId={supplier.latest_ledger.id} onClose={() => setLedgerViewOpen(false)} />
              )}
            </div>
          )}
          <div className="page-menu">
            <button className="btn btn-sm btn-excel"
              aria-expanded={reportsMenuOpen}
              onClick={() => setReportsMenuOpen(o => !o)}>
              <Icon name="spreadsheet" size={14} />
              דוחות
            </button>
            {reportsMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setReportsMenuOpen(false)} />
                <div className="page-menu-panel">
                  {/* Carries the doc-type filter too — this is the on-screen
                      table as a file, so it should match what is on screen. */}
                  <button className="page-menu-item"
                    onClick={() => {
                      setReportsMenuOpen(false);
                      documentsApi.exportExcel(supplierId, {
                        ...(dateFrom   ? { date_from: dateFrom } : {}),
                        ...(dateTo     ? { date_to:   dateTo   } : {}),
                        ...(typeFilter ? { doc_type:  typeFilter } : {}),
                      });
                    }}
                    title="תאריך, מספר מסמך, סוג, לפני/כולל מע״מ וסטטוס — רשימת המסמכים כפי שהיא מוצגת כאן">
                    <Icon name="spreadsheet" size={15} /> רשימת מסמכים
                  </button>
                  <button className="page-menu-item"
                    onClick={() => {
                      setReportsMenuOpen(false);
                      documentsApi.exportPayments(supplierId, {
                        ...(dateFrom   ? { date_from: dateFrom } : {}),
                        ...(dateTo     ? { date_to:   dateTo   } : {}),
                        ...(typeFilter ? { doc_type:  typeFilter } : {}),
                      });
                    }}
                    title="מספר מסמך, סכום לפני/כולל מע״מ, תנאי תשלום, תאריך לתשלום, סטטוס והערות — לפי הסינון שמוצג כאן">
                    <Icon name="spreadsheet" size={15} /> דוח תשלומים מפורט
                  </button>
                </div>
              </>
            )}
          </div>
          <div className="page-menu">
            <button
              className="page-menu-trigger"
              aria-label="פעולות נוספות"
              aria-expanded={pageMenuOpen}
              onClick={() => setPageMenuOpen(o => !o)}
            >⋮</button>
            {pageMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setPageMenuOpen(false)} />
                <div className="page-menu-panel">
                  {/* Same admin gate as the button above — the menu is a second
                      door to the action, not a way around its permission. */}
                  {isAdmin && (
                    <button className="page-menu-item"
                      onClick={() => { setPageMenuOpen(false); navigate('/upload', { state: { supplierId, supplierName: supplier?.name } }); }}>
                      <Icon name="plus" size={15} /> העלאת מסמך
                    </button>
                  )}
                  <button className="page-menu-item" disabled={loading}
                    onClick={() => { setPageMenuOpen(false); load(); }}
                    title="טען מחדש מהמסד נתונים">
                    <Icon name="recurring" size={15} /> רענן
                  </button>
                  <ViewAsMenuItems onDone={() => setPageMenuOpen(false)} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {dueDateFromUrl && (
        <div style={{
          background: 'var(--selected-soft-bg)',
          color: 'var(--selected-soft-ink)',
          padding: '10px 14px',
          borderRadius: 8,
          margin: '12px 0',
          fontSize: '0.9rem',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span><Icon name="calendar" size={14} /> מציג רק חשבוניות שיש לשלם ב-{fmtIsoToDisplay(dueDateFromUrl)}</span>
          <button
            type="button"
            onClick={() => navigate(`/supplier/${supplierId}`)}
            style={{
              background: 'white',
              border: '1px solid var(--selected-soft-border)',
              borderRadius: 6,
              padding: '4px 10px',
              cursor: 'pointer',
              color: 'var(--selected-soft-ink)',
              fontSize: '0.85rem',
            }}
          >
            הסר סינון
          </button>
        </div>
      )}

      {/* Unlinked delivery notes — the money bug with no other symptom.
          Admin-only (the fetch simply doesn't run for anyone else), and never a
          gate: it names the amount, offers the invoice, and offers the honest
          alternative answer ("they belong to next month's invoice"), which is
          what lets the flag reach zero instead of becoming wallpaper.
          Placed above the metric cards on purpose — those cards are among the
          figures it is telling you to distrust. */}
      {unlinked && (
        <div className="unlinked-notes-note">
          <Icon name="unlink" size={16} />
          <div className="unlinked-notes-text">
            <strong>
              {unlinked.note_count === 1
                ? 'תעודת משלוח אחת לא מקושרת לחשבונית'
                : `${unlinked.note_count} תעודות משלוח לא מקושרות לחשבונית`}
              {' · '}{formatCurrency(unlinked.total_amount)}
            </strong>
            {/* "עד שיקושרו" is a promise only a חשבונית מרכזת can keep — it is the
                one type PUT /documents/:id/links accepts. When the match is a
                plain חשבונית / חשבונית/קבלה / חשבונית זיכוי the double-count is
                just as real, so the warning stays, but the sentence must not
                point at an action the screen cannot offer. It names the
                document instead, and the dismissal below is the way out. */}
            <span>
              {unlinked.linkable
                ? 'הסכום של הספק מוצג גבוה מדי — התעודות והחשבונית נספרות פעמיים, עד שיקושרו.'
                : `הסכום של הספק מוצג גבוה מדי — התעודות ו${unlinked.invoice_doc_type || 'החשבונית'} ${unlinked.invoice_doc_num || ''} נספרות פעמיים. סוג חשבונית זה לא תומך בקישור תעודות.`}
            </span>
          </div>
          <div className="unlinked-notes-actions">
            {unlinked.linkable && (
              <button type="button" className="btn btn-sm btn-secondary"
                onClick={() => navigate(`/document/${unlinked.invoice_id}`)}>
                קשרי לחשבונית {unlinked.invoice_doc_num || ''}
              </button>
            )}
            <button type="button" className="unlinked-notes-dismiss"
              disabled={dismissing} onClick={dismissUnlinked}>
              {unlinked.note_count === 1
                ? 'שייכת לחשבונית של החודש הבא'
                : 'שייכות לחשבונית של החודש הבא'}
            </button>
          </div>
        </div>
      )}

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-value">{filtered.length}</div>
          <div className="stat-label">סה"כ מסמכים</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(totalAmount)}</div>
          <div className="stat-label">סכום כולל</div>
        </div>
      </div>

      <div className="card">
        <div className="filter-bar">
          {/* The app's filter pill (label + native select), the same control
              /suppliers and /suppliers/paid use — so it matches the date pill
              beside it instead of a field-edged form select. */}
          <label className={`docs-filter-control${typeFilter ? ' is-active' : ''}`}>
            <span className="docs-filter-microlabel">סוג מסמך</span>
            <select
              className="docs-filter-select"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            >
              <option value="">כל הסוגים</option>
              {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </label>
          {hasFilters && (
            <button className="btn btn-secondary btn-sm" onClick={clearFilters}><Icon name="close" size={15} /> נקה סינון</button>
          )}
          {/* One control for months AND arbitrary day ranges — the same
              PeriodPicker /suppliers uses, and PRESETS_MONTHLY for the same
              reason: this page is reached FROM /suppliers carrying its period,
              so the two screens should offer the same set of periods to move
              between. It replaces a חודש + מתאריך + עד תאריך trio that asked
              the user to pick which of three controls to reach for, and could
              contradict itself (typing a day silently blanked the month box).
              `.docs-filter-datewrap` carries margin-inline-start:auto, which
              anchors it to the far end of the bar exactly as it does there. */}
          <PeriodPicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
            presets={PRESETS_MONTHLY}
            variant="pill"
          />
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        ) : loadError ? (
          <InlineLoadError message="לא ניתן לטעון את מסמכי הספק" onRetry={load} />
        ) : filtered.length === 0 ? (
          <EmptyState compact={hasFilters} message={hasFilters ? 'לא נמצאו מסמכים לפי הסינון שנבחר' : 'אין מסמכים להצגה'} />
        ) : (
          <div className="table-wrapper table-scroll-mobile">
            {/* supplier-docs-table: at tablet widths the VAT split columns
                step aside so the table fits its card (index.css). */}
            <table className="supplier-docs-table">
              <thead>
                <tr>
                  <th>תאריך</th>
                  <th>מספר מסמך</th>
                  <th>סוג מסמך</th>
                  <th className="col-vat-split">לפני מע"מ</th>
                  <th className="col-vat-split">מע"מ</th>
                  <th>סה"כ</th>
                  <th>סטטוס</th>
                  <th style={{ width: 70 }}>הערות</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(d => {
                  const menuBtn = { display: 'block', width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'right', cursor: 'pointer', fontSize: '0.88rem', color: 'var(--ink)', fontFamily: 'inherit' };
                  return (
                  <tr key={d.id} style={{ background: d.payment_fully_paid ? 'var(--success-bg)' : undefined }}>
                    <td>{formatDateShort(d.date) || '—'}</td>
                    <td>
                      {/* The doc number IS the link, same as the מספר מסמך
                          column on /credits: a value in link colour reads as
                          clickable on its own, which is what let the separate
                          צפייה button go. */}
                      <button
                        type="button"
                        className="cell-link"
                        onClick={(e) => { e.stopPropagation(); navigate(`/document/${d.id}`); }}
                        title="צפה במסמך"
                        aria-label={`צפה במסמך ${d.doc_num || d.id}`}
                      >{d.doc_num || '—'}</button>
                      {d.open_credit_count > 0 && (
                        <span title={`${d.open_credit_count} זיכויים פתוחים — ${new Intl.NumberFormat('he-IL',{style:'currency',currency:'ILS',minimumFractionDigits:2,maximumFractionDigits:2}).format(d.open_credit_total)}`}
                          style={{ marginRight:6, background:'var(--warning-bg)', color:'var(--warning-ink)', borderRadius:10, padding:'1px 7px', fontSize:'0.7rem', fontWeight:700, cursor:'help', verticalAlign:'middle' }}>
                          <Icon name="credits" size={11} /> {d.open_credit_count}
                        </span>
                      )}
                      {/* Closed credits, whatever closed them. Without this the
                          amber badge just vanished on close, and a settled
                          invoice looked like one that never had a credit. A tap
                          opens the lines (a title tooltip never shows on a
                          phone); they come rendered from the server
                          (lib/closedCredits.js). */}
                      {d.closed_credit_count > 0 && (() => {
                        const lines = d.closed_credit_sources || [];
                        const open = closedCreditsOpen?.id === d.id;
                        return (
                          <span className="closed-credit-wrap">
                            <button type="button" className="closed-credit-badge" data-testid="closed-credit-badge"
                              aria-expanded={open}
                              aria-label={`${d.closed_credit_count} זיכויים שנסגרו: ${lines.join(', ')}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (open) { setClosedCreditsOpen(null); return; }
                                const r = e.currentTarget.getBoundingClientRect();
                                setClosedCreditsOpen({ id: d.id, top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
                              }}>
                              <Icon name="credits" size={11} /><Icon name="check" size={10} /> {d.closed_credit_count}
                            </button>
                            {open && (
                              <>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 198 }} onClick={() => setClosedCreditsOpen(null)} />
                                <div className="closed-credit-pop" role="note" style={{ top: closedCreditsOpen.top, right: closedCreditsOpen.right }}>
                                  <div className="closed-credit-pop-title">זיכויים שנסגרו</div>
                                  {lines.map(line => <div key={line}>{line}</div>)}
                                </div>
                              </>
                            )}
                          </span>
                        );
                      })()}
                      {paymentTrackingEnabled && d.payment_count > 0 && (() => {
                        // Derived from the TOTAL paid across every payment on
                        // the document, not the latest one: ₪2,000 then ₪1,200
                        // settles a ₪3,200 invoice, and reading only the last
                        // payment would keep calling it part-paid forever.
                        const partial = !d.payment_fully_paid;
                        const over    = d.payment_paid_total > (Math.abs(d.total) || 0) + 0.005;
                        const changed = partial || over;
                        // A real <button>, not a `title=` tooltip. The tooltip
                        // carried the amount and the method and showed neither
                        // on touch, where hover does not exist — and on desktop
                        // nobody hovers a badge to find out it was hiding
                        // something. The changed amount is now ON the badge,
                        // and the rest is one tap away.
                        return (
                          <button
                            type="button"
                            className={`payment-badge ${changed ? 'payment-badge--changed' : 'payment-badge--paid'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPaymentPanel({ doc: d, rect: e.currentTarget.getBoundingClientRect() });
                            }}
                            aria-haspopup="dialog"
                          >
                            <Icon name="check-circle" size={12} />
                            {partial ? 'שולם חלקית' : over ? 'שולם ביתר' : 'שולם'}
                            {changed && ` · ${formatCurrency(d.payment_paid_total)}`}
                          </button>
                        );
                      })()}
                    </td>
                    <td>
                      <span className="doc-type-group">
                        <span className={`badge ${['חשבונית זיכוי', 'תעודת החזרה'].includes(d.doc_type) ? 'badge-red' : 'badge-blue'}`}>
                          {d.doc_type}
                        </span>
                        {/* Admin only: the note is linked; the desktop tooltip
                            names the מרכזת (the server fills these fields for
                            admins only). */}
                        {isAdmin && d.linked_invoice_doc_num != null && (
                          <LinkedNoteMark invoiceDocNum={d.linked_invoice_doc_num} showTip={!isMobile} />
                        )}
                        {d.doc_type === 'חשבונית מרכזת' && d.linked_delivery_notes_count > 0 && (
                          <span title={`${d.linked_delivery_notes_count} תעודות משלוח מקושרות`}
                            style={{ background:'var(--info-bg)', color:'var(--info-ink)', borderRadius:10, padding:'1px 7px', fontSize:'0.7rem', fontWeight:700, cursor:'help' }}>
                            <Icon name="clip" size={11} /> {d.linked_delivery_notes_count}
                          </span>
                        )}
                      </span>
                      {/* Coverage: what the מרכזת bills for vs what we hold.
                          Shown only once the invoice actually listed its notes
                          (covered_docs_count > 0) — an invoice that itemizes
                          nothing, or one saved before this existed, gets no
                          badge rather than a green all-clear it never earned.

                          covered_docs_missing counts references with no
                          document answering them, per reference. It replaced
                          `covered − linked`, which a link to a note the invoice
                          never named (a legitimate action this UI offers) could
                          close on its own, turning the badge green while named
                          notes were still absent. */}
                      {d.doc_type === 'חשבונית מרכזת' && d.covered_docs_count > 0 && (() => {
                        const missing = d.covered_docs_missing ?? 0;
                        // "Same notes, different numbering": an admin confirmed
                        // it by amount. The server returns this only while the
                        // linked notes still reconcile (lib/coverageConfirm.js).
                        if (missing > 0 && d.coverage_confirmed_by != null) {
                          // SQLite UTC → the Israel calendar day.
                          const when = d.coverage_confirmed_at ? formatUtcDate(d.coverage_confirmed_at) : '';
                          return (
                            <span className="coverage-badge is-full"
                                  title={`אושרה לפי סכום — התעודות המקושרות תואמות למרכזת, המספור אצל הספק שונה${d.coverage_confirmed_by ? ` · ע״י ${d.coverage_confirmed_by}` : ''}${when ? ` · ${when}` : ''}`}>
                              <Icon name="check" size={11} /> מלאה (לפי סכום)
                            </span>
                          );
                        }
                        return missing > 0 ? (
                          <span className="coverage-badge is-missing"
                                title={`החשבונית מפרטת ${d.covered_docs_count} תעודות; ${missing} מהן עדיין לא קושרו או לא הועלו למערכת`}>
                            <Icon name="alert" size={11} /> חסרות {missing}
                          </span>
                        ) : (
                          <span className="coverage-badge is-full"
                                title={`כל ${d.covered_docs_count} התעודות שמפורטות בחשבונית קיימות ומקושרות`}>
                            <Icon name="check" size={11} /> מלאה
                          </span>
                        );
                      })()}
                      <DocPriceTags doc={d} isAdmin={isAdmin} />
                    </td>
                    {/* A credit always shows negative, whatever sign it was
                        stored with (a negative invoice arrives negative, some
                        חשבונית זיכוי rows positive) — hence -Math.abs. The sign
                        goes INTO formatCurrency, not prepended as a '-': Intl
                        marks it left-to-right, so in an RTL cell the minus sits
                        left of the number instead of hopping to its far end
                        (owner, 16/10/2026). `|| 0` keeps a zero from printing -0. */}
                    <td className="col-vat-split" style={isCreditDoc(d) ? { color: 'var(--danger-ink)' } : {}}>{isCreditDoc(d) ? formatCurrency(-Math.abs(d.subtotal || 0) || 0) : formatCurrency(d.subtotal)}</td>
                    <td className="col-vat-split" style={isCreditDoc(d) ? { color: 'var(--danger-ink)' } : {}}>{isCreditDoc(d) ? formatCurrency(-Math.abs(d.vat || 0) || 0) : formatCurrency(d.vat)}</td>
                    <td style={{ fontWeight: 600, ...(isCreditDoc(d) ? { color: 'var(--danger-ink)' } : {}) }}>{isCreditDoc(d) ? formatCurrency(-Math.abs(d.total || 0) || 0) : formatCurrency(d.total)}</td>
                    <td>
                      {/* Changing a document's status is admin-only on the
                          server (PUT /documents/:id/status). Showing the
                          control to a manager only produced a 403 toast. */}
                      {isAdmin ? (
                        <DocStatusControl value={d.status} onChange={status => handleStatusChange(d.id, status)} />
                      ) : (
                        <span className={`badge ${statusBadge(d.status)}`}>{d.status}</span>
                      )}
                    </td>
                    {/* Comments get the column that פעולות used to hold.
                        The count was a numeric badge stuck on the ⋮ glyph,
                        which conflated two different claims — "there is more
                        here" and "three people wrote about this row". In its
                        own column it is scannable: you can run an eye down the
                        table and see which documents have discussion.

                        The cell is a control whether or not there are notes.
                        An empty comments column on the 90% of rows with none
                        would be dead space, and making the blank state the ADD
                        affordance is what let הוסף הערה leave the ⋮ — where it
                        sat next to צפה בהערות running the identical handler,
                        two menu items that did exactly the same thing. */}
                    <td onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`note-cell${noteCount(d) > 0 ? ' is-active' : ''}`}
                        onClick={() => setModal({ referenceId: d.id, title: `מסמך ${d.doc_num || d.id}`, status: d.status })}
                        // data-tip, not title: the native tooltip waits over a
                        // second before appearing, which on an icon whose whole
                        // job is to say "you can write here" is long enough that
                        // people never find out. The CSS one shows immediately.
                        data-tip={noteCount(d) > 0 ? `${noteCount(d)} הערות` : 'הוסף הערה'}
                        aria-label={noteCount(d) > 0 ? `הערות: ${noteCount(d)}` : 'הוסף הערה'}
                      >
                        <Icon name="comment" size={14} />
                        {noteCount(d) > 0 && <span>{noteCount(d)}</span>}
                      </button>
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ position: 'relative', width: 36, textAlign: 'center' }}>
                      <button
                        className="row-menu-trigger" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--ink-subtle)', padding: '2px 4px', position: 'relative', lineHeight: 1 }}
                        onClick={() => setOpenMenu(openMenu === d.id ? null : d.id)}
                        title="פעולות"
                        aria-label="פעולות"
                      >
                        ⋮
                      </button>
                      {openMenu === d.id && (
                        <>
                          <div style={{ position: 'fixed', inset: 0, zIndex: 198 }} onClick={() => setOpenMenu(null)} />
                          <div style={{ position: 'absolute', left: 0, top: '100%', zIndex: 199, background: '#fff', border: '1px solid var(--line)', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 190, overflow: 'hidden' }}>
                            {/* Viewing leads the menu — it is the most common
                                thing to want from a row, and the ⋮ is where a
                                user looks once the צפייה button is gone.

                                הוסף הערה / צפה בהערות are NOT here any more:
                                they ran the identical handler as each other and
                                as the comments cell, so the same modal had three
                                doors. The cell is the one door. */}
                            <button className="row-menu-item" style={menuBtn}
                              onClick={() => { setOpenMenu(null); navigate(`/document/${d.id}`); }}>
                              <Icon name="eye" size={16} /> צפה במסמך
                            </button>
                            {/* Notes are reachable from the menu as well as from
                                the הערות cell — the ⋮ is where someone looks for
                                "what can I do to this row" without having
                                spotted the column.

                                Both entries open the same modal (it lists the
                                thread AND takes a new note), so צפה בהערות is
                                shown only when there is in fact something to
                                view. That is what keeps them from being the
                                pair that used to sit here: two items, identical
                                handler, identical wording-weight, one of them
                                promising content that might not exist. Now the
                                count on the second is the information. */}
                            <button className="row-menu-item" style={menuBtn}
                              onClick={() => { setOpenMenu(null); setModal({ referenceId: d.id, title: `מסמך ${d.doc_num || d.id}`, status: d.status }); }}>
                              <Icon name="comment" size={16} /> הוסף הערה
                            </button>
                            {noteCount(d) > 0 && (
                              <button className="row-menu-item" style={menuBtn}
                                onClick={() => { setOpenMenu(null); setModal({ referenceId: d.id, title: `מסמך ${d.doc_num || d.id}`, status: d.status }); }}>
                                <Icon name="eye" size={16} /> צפה בהערות ({noteCount(d)})
                              </button>
                            )}
                            {d.file_path && (
                              <>
                                <div style={{ borderTop: '1px solid var(--hairline)' }} />
                                <button className="row-menu-item" style={menuBtn}
                                  onClick={() => { setOpenMenu(null); downloadDocFile(d.file_path); }}>
                                  <Icon name="download" size={16} /> הורד קובץ
                                </button>
                              </>
                            )}
                            {paymentTrackingEnabled && (
                              <>
                                <div style={{ borderTop: '1px solid var(--hairline)' }} />
                                {/* Offered while the document still owes money,
                                    not merely while it has no payment row —
                                    that older test made "pay the rest next
                                    month" impossible, which is the whole point
                                    of being able to pay part of a bill. */}
                                {/* Never on a draft: the server refuses to pay
                                    one (approve it first), and a payment must
                                    not double as the review step. */}
                                {/* Only for someone who may record a payment
                                    (admin/hq_admin/manager — a viewer got a 403 on
                                    click), only on a document that can carry
                                    one (an invoice or a חשבונית זיכוי — a
                                    תעודת החזרה got a 400), and never on a
                                    תעודה already rolled up under a מרכזת,
                                    whose money is the invoice's. Mirrors
                                    canPay on DocumentViewPage. */}
                                {canEdit && (isPayableDocType(d.doc_type) || isCreditNoteDoc(d)) && !d.linked_to_invoice
                                  && d.payment_remaining > 0.005 && d.status !== 'טיוטה' && (
                                  <button className="row-menu-item" style={{ ...menuBtn, color: 'var(--success-ink)', fontWeight: 600 }}
                                    onClick={() => { setOpenMenu(null); setPaymentModal({ doc: d, supplierName: supplier?.name }); }}>
                                    <Icon name="card" size={16} />
                                    {d.payment_count > 0 ? 'תשלום יתרה' : 'בוצע תשלום'}
                                  </button>
                                )}
                              </>
                            )}
                            {/* Destructive actions last, in red, behind their
                                own divider — the menu already treated מחק תשלום
                                that way, so מחק מסמך sitting in its own column
                                was the odd one out rather than the safe one.
                                Both are admin-only and both confirm first. */}
                            {isAdmin && <div style={{ borderTop: '1px solid var(--hairline)' }} />}
                            {paymentTrackingEnabled && d.payment_count > 0 && isAdmin && (
                              <button className="row-menu-item" style={{ ...menuBtn, color: 'var(--danger-ink)' }}
                                onClick={() => { setOpenMenu(null); handleDeletePayment(d); }}>
                                <Icon name="trash" size={16} /> מחק תשלום
                              </button>
                            )}
                            {isAdmin && (
                              <button className="row-menu-item" style={{ ...menuBtn, color: 'var(--danger-ink)' }}
                                onClick={() => { setOpenMenu(null); handleDelete(d); }}>
                                <Icon name="trash" size={16} /> מחק מסמך
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--tint-strong)', fontWeight: 700, borderTop: '2px solid var(--outline)' }}>
                  {/* Block padding only: the inline padding comes from the
                      table's CSS so the footer tightens with the body. */}
                  <td colSpan={3} style={{ paddingBlock: 10, color: 'var(--ink)' }}>
                    סה"כ - {filtered.length} מסמכים
                  </td>
                  <td className="col-vat-split" style={{ paddingBlock: 10 }}>{formatCurrency(totalSubtotal)}</td>
                  <td className="col-vat-split" style={{ paddingBlock: 10 }}>{formatCurrency(totalVat)}</td>
                  <td style={{ paddingBlock: 10 }}>{formatCurrency(totalAmount)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <CommentsModal
          type="document"
          referenceId={modal.referenceId}
          title={modal.title}
          // A draft is hidden from customers — no customer email on it.
          internalOnlyReason={modal.status === 'טיוטה' ? DRAFT_INTERNAL_ONLY_REASON : ''}
          onClose={() => setModal(null)}
          onCommentAdded={() => setDocs(prev => prev.map(d =>
            d.id === modal.referenceId ? { ...d, comment_count: (d.comment_count || 0) + 1 } : d
          ))}
        />
      )}

      {confirmDialog}
      {deleting && (
        <DeleteDocumentDialog
          doc={deleting}
          title="מחיקת מסמך"
          confirmLabel="מחק מסמך"
          successMessage="המסמך נמחק"
          context={deletingContext(deleting)}
          onCancel={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}

      {paymentPanel && (
        <PaymentDetailPanel
          doc={paymentPanel.doc}
          anchorRect={paymentPanel.rect}
          onClose={() => setPaymentPanel(null)}
        />
      )}

      {paymentModal && (
        <PaymentModal
          supplierId={supplierId}
          supplierName={paymentModal.supplierName}
          documentId={paymentModal.doc.id}
          documentInfo={{
            doc_type:  paymentModal.doc.doc_type,
            doc_num:   paymentModal.doc.doc_num,
            total:     paymentModal.doc.total,
            // What the amount field pre-fills with and is judged against.
            remaining: paymentModal.doc.payment_remaining,
            paidTotal: paymentModal.doc.payment_paid_total,
          }}
          // The document's OWN month is what would be approved; offered only
          // while the header's lifecycle describes that month and it is not
          // yet approved (utils/paymentLifecycle.js).
          approvalOffer={approvalOfferForDoc({ lifecycle, docDate: paymentModal.doc.date, docType: paymentModal.doc.doc_type, docTotal: paymentModal.doc.total, dateFrom, dateTo })}
          onSuccess={() => { setPaymentModal(null); load(); loadLifecycle(); }}
          onClose={() => setPaymentModal(null)}
        />
      )}
    </div>
  );
}
